import { inject } from '@angular/core';
import { LocalizationService } from '@abp/ng.core';
import { Formatters } from 'angular-slickgrid';
import { FieldDefinition, FieldBuilder, MenuCategories, MenuSubcategories, MENU_CATEGORY_KEY, MENU_SUBCATEGORY_KEY, MENU_NAME_SUFFIX } from '@zendra/shared';
import { ForestrySituationService } from '../proxy/planning/controllers/forestry-situation.service';
import { LocationService } from '../proxy/planning/controllers/location.service';
import { SoilUseService } from '../proxy/planning/controllers/soil-use.service';
import { PropertyService } from '../proxy/planning/controllers/property.service';
import { ReasonService } from '@general/proxy/general/controllers/reason.service';
import { StandTypeService } from '../proxy/planning/controllers/stand-type.service';
import { ZoneService, SubregionService, RegionService} from '../proxy/planning/controllers';
import { of } from 'rxjs';
import { localizeWithFallback } from '@zendra/shared';

FieldBuilder.setNamespace('Planning');

export const STAND_FIELDS: FieldDefinition[] = [
  FieldBuilder
    .text('name', 'Entity.Stand.Name')
    .required()
    .minLength(1)
    .maxLength(100)
    .build(),
  FieldBuilder
    .number('standArea', 'Entity.Stand.StandArea')
    .required()
    .decimal(2,10)
    .build(),
  FieldBuilder
    .number('gisArea', 'Entity.Stand.GisArea')
    .decimal(2,10)
    .build(),
  FieldBuilder
    .checkbox('isStrategic', 'Entity.Stand.IsStrategic')
    .build(),
  FieldBuilder
    .checkbox('hasNearbyPopulation', 'Entity.Stand.HasNearbyPopulation')
    .build(),
  FieldBuilder
    .number('currentCycle', 'Entity.Stand.CurrentCycle')
    .build(),
  FieldBuilder
    .number('currentRotation', 'Entity.Stand.CurrentRotation')
    .build(),
  FieldBuilder
    .checkbox('isPefcCertified', 'Entity.Stand.IsPefcCertified')
    .build(),
  FieldBuilder
    .checkbox('isFscCertified', 'Entity.Stand.IsFscCertified')
    .build(),
  FieldBuilder // TODO: when the orchestrator is implemented, refactor this nested logic into it
    .foreignKey('deactivationReasonId', 'Entity.Stand.DeactivationReason', ReasonService)
    .display('name')
    .keyField('id')
    .build(),
  FieldBuilder
    .date('deactivationDate', 'Entity.Stand.DeactivationDate')
    .build(),
  FieldBuilder
    .foreignKey('forestrySituationId', 'Entity.Stand.ForestrySituation', ForestrySituationService)
    .required()
    .displayFromNested('forestrySituation.name')
    .sortBy('forestrySituation.name')
    .build(),
  FieldBuilder
    .foreignKey('previousSoilUseId', 'Entity.Stand.PreviousSoilUse', SoilUseService)
    .queryFilter({ soilUseGroupName: 'Uso previo' })
    .displayFromNested('previousSoilUse.name')
    .sortBy('previousSoilUse.name')
    .build(),
  FieldBuilder
    .foreignKey('locationId', 'Entity.Location', LocationService)
    .required()
    .displayFromNested('location.name')
    .sortBy('location.name')
    .hideInChildForms()
    .build(),
  FieldBuilder
    .foreignKey('standTypeId', 'Entity.StandType', StandTypeService)
    .required()
    .displayFromNested('standType.name')
    .sortBy('standType.name')
    .build(),
  FieldBuilder
    .foreignKey('regionId', 'Entity.Region', RegionService)
    .displayFromNested('property.zone.subregion.region.name')
    .sortBy('property.zone.subregion.region.name')
    .prefilter({ multiple: true })
    .readOnly()
    .build(),
  FieldBuilder
    .foreignKey('subregionId', 'Entity.Subregion', SubregionService)
    .displayFromNested('property.zone.subregion.name')
    .sortBy('property.zone.subregion.name')
    .prefilter({ dependsOn: ['regionId'], multiple: true })
    .readOnly()
    .build(),
  FieldBuilder
    .foreignKey('zoneId', 'Entity.Zone', ZoneService)
    .displayFromNested('property.zone.name')
    .sortBy('property.zone.name')
    .prefilter({ dependsOn: ['subregionId'], multiple: true })
    .readOnly()
    .build(),
  FieldBuilder
    .foreignKey('propertyId', 'Entity.Property', PropertyService)
    .required()
    .displayFromNested('property.name')
    .sortBy('property.name')
    .prefilter({ dependsOn: ['zoneId'], multiple: true })
    .build(),
  FieldBuilder
    .dropdown('locationIsActive', 'Entity.Location.IsActive')
    .options([
      { key: 'true', value: 'Yes' },
      { key: 'false', value: 'No' },
    ])
    .optionsLoader(() => {
      const localizationService = inject(LocalizationService);
      return of([
        { key: 'true', value: localizeWithFallback(localizationService, 'AbpUi::Yes') },
        { key: 'false', value: localizeWithFallback(localizationService, 'AbpUi::No') },
      ]);
    })
    .prefilter({ queryKey: 'locationIsActive', defaultValue: 'true' })
    .readOnly()
    .formatter((_row: number, _cell: number, _value: unknown, _colDef: unknown, dataContext: any, _grid: any) => {
      const value = dataContext?.location?.isActive ?? dataContext?.locationIsActive ?? _value;
      return Formatters.checkmarkMaterial(_row, _cell, value, _colDef as any, dataContext, _grid);
    })
    .build(),
];

export const STAND_CONFIG = {
  entityName: 'Stand',
  localizationKey: 'Planning::Entity.Stand',
  permission: 'PlanningLocation.Stands',
  fields: STAND_FIELDS,
  showInMenu: true,
  menuCategory: MENU_CATEGORY_KEY + MenuCategories.Planning + MENU_NAME_SUFFIX,
  menuSubcategory: MENU_SUBCATEGORY_KEY + MenuSubcategories.Location + MENU_NAME_SUFFIX,
};
